// A stack drawn as a rail: one row per pull request, base first, every row
// at the same indent, joined by one vertical line. Where the stack forks, a
// branch is drawn one lane over, off the rail; a fork within a branch stays
// on the branch's lane. Every row whose base is not the row directly above
// names it. Members not in the section are kept in their places as asides,
// and a run of them is one row; a context row waiting on the viewer is a
// full row, unless the dashboard's filter leaves it out (`PrNode.filtered`).
// Pure: the dashboard renders what comes back.
import { sameLogin } from './attention';
import type { MissingNode, PrNode, Stack, StackNode, StackRow } from './stacks';

interface RailBase {
  /** 0 on the stack's rail, 1 on a branch off it. */
  lane: 0 | 1;
  /** The first row of a branch off lane 0, drawn curving off the rail. */
  elbow: boolean;
  /** What the row builds on; null for the bottom of the stack. */
  base: BuildsOn | null;
  /** The stack positions the row covers, `index` to `to` inclusive. */
  index: number;
  to: number;
  /** The last row on its lane's line: the line stops at it. */
  end: boolean;
}

/** The pull request a row builds on. */
export interface BuildsOn {
  /** Its stack position (the last of a gathered row). */
  index: number;
  /** Its number, when known (a missing member GitHub did not number has none). */
  pr: RepoNumber | null;
  /** It is the row directly above, so the order alone shows it. */
  above: boolean;
}

/** One of the section's pull requests, or a context row waiting on the viewer. */
export interface RailPr<R extends StackRow> extends RailBase {
  kind: 'pr';
  node: PrNode<R>;
}

/** A pull request number in a repository. */
export interface RepoNumber {
  repo: string;
  number: number;
}

/** Consecutive members not in the section: context rows and members not loaded, each building on the last. */
export interface RailAside<R extends StackRow> extends RailBase {
  kind: 'aside';
  /** Bottom-up. */
  members: (PrNode<R> | MissingNode<R>)[];
  /** The first and last pull request of the run, when both are known. */
  first: RepoNumber | null;
  last: RepoNumber | null;
  /** Every member is one no section loaded. */
  onlyMissing: boolean;
}

export type RailRow<R extends StackRow> = RailPr<R> | RailAside<R>;

/** The last stack position a node covers (a run of missing members covers several). */
const lastIndex = <R extends StackRow>(n: StackNode<R>) => (n.kind === 'missing' ? n.index + n.to - n.from : n.index);

function ends<R extends StackRow>(n: StackNode<R>): { first: RepoNumber | null; last: RepoNumber | null } {
  if (n.kind === 'pr') {
    const at = { repo: n.row.pr.repository.nameWithOwner, number: n.row.pr.number };
    return { first: at, last: at };
  }
  return {
    first: n.first === null ? null : { repo: n.repo, number: n.first },
    last: n.last === null ? null : { repo: n.repo, number: n.last },
  };
}

/** The stack's rows, top to bottom. */
export function rail<R extends StackRow>(stack: Stack<R>): RailRow<R>[] {
  const rows: RailRow<R>[] = [];
  /** `branch` is the lane of the pull request a branch starts from, or null when `n` continues its base's line. */
  const walk = (n: StackNode<R>, lane: 0 | 1, base: StackNode<R> | null, branch: 0 | 1 | null) => {
    const elbow = branch === 0;
    const prev = rows[rows.length - 1];
    const on: BuildsOn | null = base && {
      index: lastIndex(base),
      pr: ends(base).last,
      above: !!prev && (prev.kind === 'pr' ? prev.node === base : prev.members[prev.members.length - 1] === base),
    };
    if (n.kind === 'pr' && !(n.context && (!n.yours || n.filtered))) {
      rows.push({ kind: 'pr', lane, elbow, base: on, index: n.index, to: n.index, end: false, node: n });
    } else if (branch === null && prev?.kind === 'aside' && prev.lane === lane && on?.above) {
      prev.members.push(n);
      prev.to = lastIndex(n);
      prev.last = ends(n).last;
      prev.onlyMissing &&= n.kind === 'missing';
    } else {
      rows.push({ kind: 'aside', lane, elbow, base: on, index: n.index, to: lastIndex(n), end: false, members: [n], ...ends(n), onlyMissing: n.kind === 'missing' });
    }
    const kids = n.children;
    kids.forEach((c, i) => {
      const trunk = i === kids.length - 1;
      walk(c, trunk ? lane : 1, n, trunk ? null : lane);
    });
  };
  walk(stack.root, 0, null, null);

  rows.forEach((r, i) => {
    const next = rows[i + 1];
    r.end = r.lane === 0 ? !rows.slice(i + 1).some((x) => x.lane === 0) : !next || next.lane === 0 || next.elbow;
  });
  return rows;
}

const numbered = (x: RepoNumber, crossRepo: boolean) => `${crossRepo ? x.repo : ''}#${x.number}`;

/** What a row builds on, in words: `builds on #497`, or by stack position when GitHub did not number it. */
export function buildsOnText(base: BuildsOn, crossRepo: boolean): string {
  return `builds on ${base.pr ? numbered(base.pr, crossRepo) : `position ${base.index}`}`;
}

/**
 * The members not loaded that the stack's start builds on, in words (their
 * attention is unknown, so the viewer may be wanted lower down); null when
 * there are none or no start.
 */
export function belowStart<R extends StackRow>(stack: Stack<R>): string | null {
  const path = (n: StackNode<R>): StackNode<R>[] | null => {
    if (n === stack.start) return [];
    for (const c of n.children) {
      const p = path(c);
      if (p) return [n, ...p];
    }
    return null;
  };
  const missing = (stack.start ? (path(stack.root) ?? []) : []).filter((n): n is MissingNode<R> => n.kind === 'missing');
  const count = missing.reduce((sum, n) => sum + n.to - n.from + 1, 0);
  if (!count) return null;
  if (count === 1 && missing[0].first !== null) return `builds on ${numbered({ repo: missing[0].repo, number: missing[0].first }, stack.repos.length > 1)}, not loaded`;
  return `builds on ${count} pull request${count === 1 ? '' : 's'} not loaded`;
}

/** Whose turn it is on a pull request, from its attention set. */
export type Turn =
  | { kind: 'you' }
  | { kind: 'author' }
  | { kind: 'others'; logins: string[] }
  | { kind: 'none' }
  | { kind: 'unknown' };

/** The viewer's turn when they are in `attention`; the author's when only the author is; else who is in it. */
export function turn(attention: readonly { login: string }[] | null, author: string | null, me: string): Turn {
  if (!attention) return { kind: 'unknown' };
  if (!attention.length) return { kind: 'none' };
  if (attention.some((m) => sameLogin(m.login, me))) return { kind: 'you' };
  if (author && attention.every((m) => sameLogin(m.login, author))) return { kind: 'author' };
  return { kind: 'others', logins: attention.map((m) => m.login) };
}

/** A count in at most three characters past 999: `2.4k`, `43k`, `1.3M`. */
export function compactCount(n: number): string {
  if (n < 1000) return String(n);
  if (n < 10_000) return `${Math.round(n / 100) / 10}k`;
  if (n < 999_500) return `${Math.round(n / 1000)}k`;
  if (n < 10_000_000) return `${Math.round(n / 100_000) / 10}M`;
  return `${Math.round(n / 1_000_000)}M`;
}
